<template>
  <li class="hospitalCassette" :class="{ '-narrow': narrow }">
    <div>
      <p class="hospitalCassette_fes" v-if="isParticipated">
        イベント出展予定！
      </p>
      <div class="hospitalCassette_inner">
        <div
          class="hospitalCassette_header"
          :class="{ hasCheckbox: canRequestCheck }"
        >
          <p class="hospitalCassette_ttl">
            {{ hospital.name }}
          </p>
          <HospitalLabel :items="hospital.hospitalTags" />
        </div>
        <div class="hospitalCassette_img -isPC">
          <img
            v-if="hospital.imageSrc"
            :src="hospital.imageSrc"
            :alt="hospital.name"
          />
          <img
            v-else
            src="@/assets/images/common/noimg.svg"
            :alt="hospital.name"
          />
        </div>
        <div class="hospitalCassette_info">
          <div class="hospitalCassette_img">
            <img
              v-if="hospital.imageSrc"
              :src="hospital.imageSrc"
              :alt="hospital.name"
            />
            <img
              v-else
              src="@/assets/images/common/noimg.svg"
              :alt="hospital.name"
            />
          </div>
          <div class="hospitalCassette_about">
            <dl
              class="hospitalCassette_data"
              v-if="hospital.prefecture && hospital.address"
            >
              <dt>所在地</dt>
              <dd>{{ hospital.prefecture }}{{ hospital.address }}</dd>
            </dl>
            <dl class="hospitalCassette_data" v-if="hospital.hospitalClass">
              <dt>病院種別</dt>
              <dd>
                <!-- @TODO ハイライトを付ける場合は .highlight を付与 -->
                <span>{{ hospital.hospitalClass }}</span>
              </dd>
            </dl>
            <dl class="hospitalCassette_data" v-if="hospital.displayBedCount">
              <dt>病床数</dt>
              <dd>{{ hospital.displayBedCount }}床</dd>
            </dl>
            <dl
              class="hospitalCassette_data"
              v-if="hospital.displayDoctorCount"
            >
              <dt>医師数</dt>
              <dd>{{ hospital.displayDoctorCount }}人</dd>
            </dl>
          </div>
        </div>

        <div
          class="hospitalCassette_flex"
          v-if="hospital.shouldShowScore || hospital.featureTags.length > 0"
        >
          <div class="hospitalCassette_score" v-if="hospital.shouldShowScore">
            <HospitalDetailGraph
              :data="hospital.hospitalScores"
              class="hospitalCassette_graph"
            />
            <button
              @click="showScoringCriteriaModal"
              class="hospitalCassette_popup"
            >
              <img src="@/assets/images/icon/question-gray.svg" alt="" />
            </button>
          </div>
          <HospitalTag
            v-if="hospital.featureTags.length > 0 && shouldShowFeatures"
            class="hospitalCassette_feature"
            :tags="hospital.featureTags"
          />
        </div>
        <div class="hospitalCassette_btns" v-if="!shouldShowRequestCheckbox">
          <Button
            :href="`/search/hospital/detail/${hospital.id}`"
            :label="
              canShowHospitalDetail ? '病院詳細を見る' : '掲載が終了しました'
            "
            :handleClick="goToHospitalDetail(hospital.id)"
            :disabled="!canShowHospitalDetail"
            primary
          />
          <Button
            v-if="shouldShowRequestButton"
            label="資料請求"
            :href="`/document_request/${hospital.id}/input`"
            :handleClick="goToDocumentRequest(hospital.id)"
            primary
          />
          <FavoriteButton
            :label="favoriteButtonLabel"
            :is-active="isAlreadyFavorite"
            :handleClick="onFavoriteButtonClicked"
          />
        </div>
      </div>
    </div>

    <button
      v-if="!shouldShowRequestCheckbox"
      class="hospitalCassette_fav"
      :class="{ '-active': isAlreadyFavorite }"
      type="button"
      @click="onFavoriteButtonClicked()"
    >
      {{ favoriteButtonLabel }}
    </button>

    <div
      class="hospitalCassette_date"
      v-if="hospital.displayPublicationDeadline"
    >
      <dl>
        <dt>掲載期限</dt>
        <dd>{{ hospital.displayPublicationDeadline }}</dd>
      </dl>
    </div>

    <div class="hospitalCassette_checkbox" v-if="canRequestCheck">
      <input
        type="checkbox"
        :name="`${hospital.id}_check`"
        :id="`${hospital.id}_check`"
        :value="hospital.id"
        @change="onHospitalSelectionChanged($event)"
      /><label
        :for="`${hospital.id}_check`"
        class="hospitalCassette_checkboxLabel"
      ></label>
    </div>
  </li>
</template>

<script lang="ts">
import { SetupContext, computed, defineComponent } from 'vue'
import { useStore } from 'vuex'
import { HomeHospitalItem } from '@/types/HomeHospital'
import Button from '@/components/elements/Button.vue'
import FavoriteButton from '@/components/elements/FavoriteButton.vue'
import HospitalLabel from '@/components/elements/HospitalLabel.vue'
import HospitalTag from '@/components/elements/HospitalTag.vue'
import HospitalDetailGraph from '@/components/block/HospitalDetailGraph.vue'
import { useRouter } from 'vue-router'

// NOTE: リポジトリ層でのデータ変換の実例を示すため、あえてHospitalCassetteから分割しています。

export default defineComponent({
  name: 'HomeHospitalCassette',

  props: {
    hospital: {
      type: Object as () => HomeHospitalItem,
      default: () => ({})
    },
    narrow: {
      type: Boolean,
      default: false
    },
    shouldShowRequestCheckbox: {
      type: Boolean,
      default: false
    },
    shouldShowFeatures: {
      type: Boolean,
      default: true
    },
    shouldShowRequestButton: {
      type: Boolean,
      default: false
    }
  },

  components: {
    Button,
    FavoriteButton,
    HospitalLabel,
    HospitalTag,
    HospitalDetailGraph
  },

  setup(props, context: SetupContext) {
    const store = useStore()
    const router = useRouter()

    // お気に入りに追加済みの病院の場合お気に入り済みに変更
    const isAlreadyFavorite = computed(() => {
      return store.getters['home/isAlreadyFavorite'](props.hospital.id)
    })

    const favoriteButtonLabel = computed(() => {
      return isAlreadyFavorite.value ? 'お気に入り登録済み' : 'お気に入りに保存'
    })

    // FIXME: マジックナンバー。paid_flg=1やactive_flg=3の意味をコードから読み取れないので定数を定義する
    const canRequestCheck = computed((): boolean => {
      return (
        props.shouldShowRequestCheckbox &&
        props.hospital.paidFlg === 1 &&
        props.hospital.activeFlg === 3
      )
    })

    // FIXME: これもマジックナンバー
    const canShowHospitalDetail = computed((): boolean => {
      return props.hospital.activeFlg === 3
    })

    const isParticipated = computed(() => {
      return store.getters['home/isParticipated'](props.hospital.id)
    })

    const goToHospitalDetail = (hospitalId: number) => () => {
      router.push(`/search/hospital/detail/${hospitalId}`)
    }

    const goToDocumentRequest = (hospitalId: number) => () => {
      router.push(`/document_request/${hospitalId}/input`)
    }

    const onFavoriteButtonClicked = () => {
      if (isAlreadyFavorite.value) {
        store.dispatch('removeFavorite', { id: props.hospital.id })
      } else {
        store.dispatch('addFavorite', { id: props.hospital.id })
      }
    }

    const onHospitalSelectionChanged = (e: Event) => {
      if (!(e.target instanceof HTMLInputElement)) return
      context.emit('call-input-change', {
        value: e.target.value,
        checked: e.target.checked
      })
    }

    const showScoringCriteriaModal = () => {
      store.dispatch('showScoringCriteriaModal')
    }

    return {
      isAlreadyFavorite,
      favoriteButtonLabel,
      canRequestCheck,
      canShowHospitalDetail,
      isParticipated,
      goToHospitalDetail,
      goToDocumentRequest,
      onFavoriteButtonClicked,
      onHospitalSelectionChanged,
      showScoringCriteriaModal
    }
  }
})
</script>

<style lang="scss" scoped>
@use 'src/styles/abstracts/mixins';
@use 'src/styles/abstracts/variables' as var;

.hospitalCassette {
  position: relative;
  border-radius: 5px;
  background: #fff;
  border: 1px solid rgb(228, 228, 228);
  box-shadow: 0px 3px 6px rgba(0, 0, 0, 0.08);
  font-size: 1.3rem;

  & + & {
    margin-top: 15px;
    @include mixins.media() {
      margin-top: 24px;
    }
  }

  &_fes {
    padding: 5px;
    color: #444;
    background: linear-gradient(to right, #ffe869, #caf58f);
    text-align: center;
    font-size: 1.1rem;
    font-weight: 700;
  }

  &_fav {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    // text-align: center;
    background-color: #f5f5f5;
    padding: 10px;
    font-size: 1.3rem;
    @include mixins.media() {
      display: none;
    }

    &::before {
      content: '';
      display: block;
      margin-right: 8px;
      width: 20px;
      height: 18px;
      background-image: url('~@/assets/icon/heart.svg');
      background-size: 20px 18px;
    }

    &.-active::before {
      background-image: url('~@/assets/icon/heart_on.svg');
      background-size: 20px 18px;
    }
  }

  &_inner {
    padding: 20px;
    position: relative;
    @include mixins.media() {
      padding-bottom: 25px;
    }
  }

  &_ttl {
    font-size: 1.8rem;
    font-weight: 500;
    line-height: 1.5;
    margin-bottom: 10px;
  }

  &_tag {
    display: flex;
    flex-wrap: wrap;
    justify-content: flex-start;

    > li {
      font-size: 1rem;
      font-weight: 500;
      background-color: #fffccb;
      padding: 4px;
      line-height: 1;
      margin-right: 4px;
      margin-bottom: 4px;
    }
  }

  &_info {
    margin-top: 15px;
    overflow: hidden;
    position: relative;
  }

  &_img {
    width: 70px;
    position: absolute;
    right: 0px;
    top: 0;
    @include mixins.media() {
      display: none;
    }
    &.-isPC {
      right: 20px;
      top: 24px;
      width: 25.2%;
      display: none;
      @include mixins.media() {
        display: block;
      }
    }
  }

  &_about {
    margin-top: -6px;
    padding-right: 70px;
    line-height: 1.5;

    @include mixins.media() {
      display: flex;
      flex-wrap: wrap;
      width: 74.8%;
      padding-right: 0;
    }

    dt {
      flex-basis: 80px;
      flex-shrink: 0;
      font-weight: 500;
    }

    dd {
      color: #666;
      padding-right: 30px;
    }
  }

  &_data {
    display: flex;
    margin-top: 6px;

    @include mixins.media() {
      font-size: 1.4rem;
      flex-basis: 66.6%;
      max-width: 66.6%;

      &:nth-child(1) {
        order: 1;
      }
      &:nth-child(2) {
        order: 3;
      }
      &:nth-child(3) {
        order: 2;
      }
      &:nth-child(4) {
        order: 4;
      }
      &:nth-child(3),
      &:nth-child(4) {
        flex-basis: 33.3%;
        max-width: 33.3%;
        margin-left: auto;

        dt {
          flex-basis: 70px;
        }
      }
    }
  }

  &_departments {
    display: block;
    line-height: 1.5;
  }

  &_flex {
    @include mixins.media() {
      display: flex;
      align-items: flex-start;
      margin-top: 28px;
    }
  }

  &_score {
    margin-top: 10px;
    display: flex;
    align-items: flex-start;
    justify-content: space-between;

    @include mixins.media() {
      margin-top: -6px;
      margin-right: 40px;
      flex-basis: 336px;
      max-width: 336px;
      flex-shrink: 0;
    }
  }

  &_graph {
    margin-top: 0;
    margin-right: 10px;
    max-width: 360px;

    @include mixins.media() {
      margin-right: 15px;
    }
  }

  &_popup {
    margin-top: 5px;
    min-width: 16px;

    img {
      display: block;
      width: 16px;
      height: 16px;
    }
  }

  &_feature {
    margin-top: 20px;

    @include mixins.media() {
      margin-top: 0;
      flex: 1;
    }
  }

  &_btns {
    margin-top: 24px;

    @include mixins.media() {
      display: flex;
      justify-content: center;
    }

    * {
      max-width: 71%;
      height: 40px;
      min-height: 40px;

      @include mixins.media() {
        margin-left: 0;
        margin-right: 0;
        max-width: 215px;
      }
    }

    .button + .button {
      margin-top: 16px;

      @include mixins.media() {
        display: flex;
        margin-top: 0;
        margin-left: 20px;
      }
    }

    @include mixins.mediaMax() {
      > .button:last-child {
        display: none;
      }
    }
  }

  &_header {
    @include mixins.media() {
      width: calc(74.8% - 20px);
    }
  }

  &_header.hasCheckbox {
    margin-left: 60px;
  }

  &_checkbox {
    position: absolute;
    top: 40px;
    left: 30px;
    width: 20px;
    height: 20px;
    // transform: translate(30px, 80px);

    & > input[type='checkbox'] {
      display: none;
    }
  }

  &_checkboxLabel {
    position: relative;
    transition: background 0.1s ease, border 0.1s ease;

    &::before {
      display: inline-block;
      content: '';
      width: 20px;
      height: 20px;
      background-color: var.$color-border-gray;
      border-radius: 4px;
      flex-shrink: 0;
      transition: background 0.1s ease;
    }

    &::after {
      opacity: 0;
      position: absolute;
      top: 0;
      left: 0;
      display: inline-block;
      content: '';
      width: 12px;
      height: 8px;
      border-left: 2px solid #fff;
      border-bottom: 2px solid #fff;
      transform: rotate(-45deg) translate(4px, 2px);
      transition: opacity 0.1s ease;
    }

    .hospitalCassette_checkbox > input[type='checkbox']:checked + & {
      &::before {
        background-color: var.$color-main;
      }
      &::after {
        opacity: 1;
      }
    }
  }

  &_date {
    margin-top: 10px;
    margin-bottom: 20px;
    font-size: 1.2rem;
    text-align: center;

    > dl {
      &::before {
        display: inline-block;
        content: '';
        background: url('~@/assets/images/icon/time.svg') no-repeat;
        background-size: 100% 100%;
        width: 17px;
        height: 17px;
        margin-right: 5px;
        vertical-align: -4px;
      }

      > dt {
        display: inline;

        &::after {
          content: '：';
        }
      }
      > dd {
        display: inline;
      }
    }
  }
}

@include mixins.media() {
  .hospitalCassette.-narrow {
    .hospitalCassette_header {
      width: 100%;
    }
    .hospitalCassette_info {
      display: flex;
      flex-direction: row-reverse;
    }
    .hospitalCassette_about {
      width: 100%;
    }
    .hospitalCassette_data {
      width: 100%;
      flex-basis: 100%;
      max-width: 100%;
      order: unset;
      dt {
        width: 80px;
        flex-basis: 80px;
      }
    }
    .hospitalCassette_img {
      display: block;
      width: 150px;
      min-width: 150px;
      position: static;
      flex-shrink: 0;
    }
    .hospitalCassette_img.-isPC {
      display: none;
    }
    .hospitalCassette_flex {
      display: block;
      margin-top: 15px;
    }
    .hospitalCassette_feature {
      margin-left: 0;
      margin-top: 16px;
    }
  }
}

.highlight {
  background-color: #fffccb;
}
</style>
